import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "../../hooks/api/use-api";
import Icon, { type IconName } from "../custom/Icon";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";

interface INavigationItem {
	icon: IconName;
	path: string;
	label: string;
}

interface MobileMenuProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export default function MobileMenu({ open, onOpenChange }: MobileMenuProps) {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { auth, admins } = useApi();
	const { data: session } = auth.useSession();
	const { data: adminCheck } = admins.checkCurrentUserIsAdmin();

	const userNavigationItems: INavigationItem[] = [
		{ icon: "LayoutGrid", path: "/", label: t("nav.dashboard") },
		{ icon: "HandPlatter", path: "/orders", label: t("nav.orders") },
		{ icon: "User", path: "/profile", label: t("nav.profile") },
		{ icon: "Settings", path: "/settings", label: t("nav.settings") },
		{ icon: "CircleHelp", path: "/support", label: t("nav.support") },
	];

	const adminNavigationItems: INavigationItem[] = [
		{ icon: "LayoutDashboard", path: "/admin", label: t("nav.dashboard") },
		{ icon: "HandPlatter", path: "/admin/orders", label: t("nav.orders") },
		{ icon: "Users", path: "/admin/users", label: t("nav.users") },
		{ icon: "Shield", path: "/admin/admins", label: t("nav.admins") },
		{ icon: "Menu", path: "/admin/menu", label: t("nav.menu") },
		{ icon: "Settings", path: "/admin/settings", label: t("nav.settings") },
	];

	const navigationItems = adminCheck?.isAdmin
		? adminNavigationItems
		: userNavigationItems;

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="left" className="w-80 p-0">
				<SheetHeader className="border-b p-6 flex flex-row items-center justify-between">
					<SheetTitle className="text-left">
						{t("common.navigation")}
					</SheetTitle>
				</SheetHeader>
				<div className="flex flex-col gap-2 p-4">
					{navigationItems.map((item) => (
						<Button
							key={item.path}
							variant={pathname === item.path ? "primary" : "ghost"}
							className="justify-start gap-3 h-12"
							asChild
							onClick={() => onOpenChange(false)}
						>
							<Link to={item.path}>
								<Icon name={item.icon} className="size-5" />
								{item.label}
							</Link>
						</Button>
					))}
				</div>

				<div className="border-t mt-auto p-4">
					{session?.user ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="w-full flex items-center gap-3 rounded-lg py-4"
								>
									<Avatar className="size-10">
										<AvatarImage
											src={session.user.image || undefined}
											alt={t("common.user_image_alt")}
										/>
										<AvatarFallback className="bg-primary/10 text-primary">
											{session.user.name
												? session.user.name.charAt(0).toUpperCase()
												: "U"}
										</AvatarFallback>
									</Avatar>
									<div className="text-start flex-1">
										<div className="text-sm font-medium">
											{session.user.name}
										</div>
										<div className="text-xs text-muted-foreground">
											@{session.user.email.split("@")[0]}
										</div>
									</div>
									<Icon name="ChevronUp" className="size-4 text-primary" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" className="w-72 mb-6">
								<DropdownMenuLabel>{t("common.my_account")}</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link
										to="/profile"
										className={`flex items-center ${pathname === "/profile" ? "bg-primary/10 text-primary" : ""}`}
										onClick={() => onOpenChange(false)}
									>
										<Icon name="User" className="w-4 h-4 mr-3" />
										{t("nav.profile")}
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/settings"
										className={`flex items-center ${pathname === "/settings" ? "bg-primary/10 text-primary" : ""}`}
										onClick={() => onOpenChange(false)}
									>
										<Icon name="Settings" className="w-4 h-4 mr-3" />
										{t("nav.settings")}
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/support"
										className={`flex items-center ${pathname === "/support" ? "bg-primary/10 text-primary" : ""}`}
										onClick={() => onOpenChange(false)}
									>
										<Icon name="Info" className="w-4 h-4 mr-3" />
										{t("nav.support")}
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									className="text-destructive focus:text-destructive"
									onClick={() => auth.signOut()}
								>
									<Icon name="LogOut" className="w-4 h-4 mr-3" />
									{t("common.sign_out")}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<Button
							variant="primary"
							className="w-full"
							asChild
							onClick={() => onOpenChange(false)}
						>
							<Link to="/auth/signin">
								<Icon name="LogIn" className="w-4 h-4 mr-2" />
								{t("common.sign_in")}
							</Link>
						</Button>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
